// Team Stars beacon: a small floating star above spawn that changes colour at each milestone.
import * as THREE from 'three';
import { nameTag } from './models.js';

const TIERS = [[10, '#d98a45'], [25, '#cfd8e6'], [50, '#ffd23f'], [100, '#4ff5ec']]; // bronze, silver, gold, diamond
const POP = 0.8;
const _c = new THREE.Color();

// Pure: the colour for a star count, or rainbow from 100 stars.
export function beaconTier(stars) {
  for (const [below, color] of TIERS) if (stars < below) return { color, rainbow: false };
  return { color: '#ffffff', rainbow: true };
}

function starGeometry(outer = 0.6, inner = 0.27) {
  const s = new THREE.Shape();
  for (let i = 0; i < 10; i++) {
    const r = i % 2 ? inner : outer, a = Math.PI / 2 + (i / 10) * Math.PI * 2;
    if (i) s.lineTo(Math.cos(a) * r, Math.sin(a) * r);
    else s.moveTo(Math.cos(a) * r, Math.sin(a) * r);
  }
  s.closePath();
  const g = new THREE.ExtrudeGeometry(s, { depth: 0.22, bevelEnabled: true, bevelThickness: 0.06, bevelSize: 0.05, bevelSegments: 1 });
  g.center();
  return g;
}

export class StarBeacon {
  constructor(parentGroup, { x = 0.5, y = 45.5, z = -3.5 } = {}) {
    this.parent = parentGroup;
    this.group = new THREE.Group();
    this.group.position.set(x, y, z);
    this.group.visible = false;
    this.spin = new THREE.Group();
    this.geo = starGeometry();
    this.mat = new THREE.MeshLambertMaterial({ color: '#ffd23f', emissive: '#ffd23f', emissiveIntensity: 0.55 });
    this.glowMat = new THREE.MeshBasicMaterial({ color: '#ffd23f', transparent: true, opacity: 0.22, depthWrite: false, blending: THREE.AdditiveBlending });
    const star = new THREE.Mesh(this.geo, this.mat);
    const glow = new THREE.Mesh(this.geo, this.glowMat);
    glow.scale.setScalar(1.3);
    this.spin.add(star, glow);
    this.group.add(this.spin);
    parentGroup.add(this.group);
    this.stars = 0;
    this.tag = null;
    this.rainbow = false;
    this.pop = 0;
    this.t = 0;
  }

  set(stars) {
    const n = Math.max(0, Math.floor(Number(stars)) || 0);
    this.group.visible = n > 0;
    if (n === this.stars) return;
    this.stars = n;
    const tier = beaconTier(n);
    this.rainbow = tier.rainbow;
    if (!tier.rainbow) this.tint(tier.color);
    if (this.tag) {
      this.group.remove(this.tag);
      this.tag.material.map.dispose();
      this.tag.material.dispose();
      this.tag = null;
    }
    if (n > 0) {
      this.tag = nameTag(`⭐ ${n} TEAM STAR${n === 1 ? '' : 'S'}`, { crown: false, color: '#ffe9a6', scale: 1.9 });
      this.tag.position.y = 1.25;
      this.group.add(this.tag);
    }
  }

  tint(color) {
    this.mat.color.set(color);
    this.mat.emissive.copy(this.mat.color);
    this.glowMat.color.copy(this.mat.color);
  }

  update(dt, time) {
    this.t += dt;
    if (!this.group.visible) return;
    const t = time ?? this.t;
    this.spin.rotation.y += dt * (this.pop > 0 ? 7 : 0.9);
    this.spin.position.y = Math.sin(t * 1.4) * 0.15;
    if (this.pop > 0) this.pop = Math.max(0, this.pop - dt);
    this.spin.scale.setScalar(1 + 0.6 * Math.sin((this.pop > 0 ? 1 - this.pop / POP : 0) * Math.PI));
    if (this.rainbow) this.tint(_c.setHSL((t * 0.12) % 1, 0.9, 0.62));
  }

  celebrate() {
    this.pop = POP;
  }

  dispose() {
    this.parent.remove(this.group);
    this.geo.dispose();
    this.mat.dispose();
    this.glowMat.dispose();
    if (this.tag) {
      this.tag.material.map.dispose();
      this.tag.material.dispose();
      this.tag = null;
    }
  }
}
